import React, { memo } from "react";
import { Handle, Position } from "reactflow";
import { resolveAvatarUrl } from "@/lib/api";

const STATUS_COLOR = {
  synced: "#0A7029",
  pending: "#D83B01",
  pending_push: "#D83B01",
  local: "#2F2F2F",
  error: "#C50F1F",
};

function EmployeeNodeImpl({ data, selected }) {
  const emp = data.employee;
  const onClick = () => data.onSelect?.(emp);
  const avatar = resolveAvatarUrl(emp);
  const status = emp.sync_status || (emp.source === "microsoft" ? "synced" : "local");
  const scenarioChanged = !!emp._scenarioChanged;
  const scenarioAdded = !!emp._scenarioAdded;
  const initials = (emp.name || "?")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      onClick={onClick}
      data-testid={`org-node-${emp.id}`}
      className={`group relative w-[240px] cursor-pointer border bg-white px-4 py-3 transition-all ${
        selected
          ? "border-[#002FA7] shadow-[0_0_0_2px_rgba(0,47,167,0.2)]"
          : scenarioAdded
          ? "border-[#0A7029] shadow-[0_0_0_2px_rgba(10,112,41,0.18)]"
          : scenarioChanged
          ? "border-[#5B5FC7] shadow-[0_0_0_2px_rgba(91,95,199,0.18)]"
          : "border-neutral-300 hover:border-neutral-900"
      }`}
    >
      <Handle type="target" position={Position.Top} className="!h-1 !w-2 !border-0 !bg-neutral-400" />
      <span
        title={`Sync: ${status}`}
        className="absolute right-2 top-2 h-1.5 w-1.5"
        style={{ background: STATUS_COLOR[status] || "#999" }}
      />
      {scenarioAdded && (
        <span className="absolute -left-1 top-2 bg-[#0A7029] px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-white" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          new
        </span>
      )}
      {scenarioChanged && (
        <span className="absolute -left-1 top-2 bg-[#5B5FC7] px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-white" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          edited
        </span>
      )}
      <div className="flex items-start gap-3">
        {avatar ? (
          <img
            src={avatar}
            alt={emp.name}
            className="h-10 w-10 shrink-0 object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-[#002FA7] text-[11px] font-semibold text-white">
            {initials}
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
            {emp.name}
          </p>
          <p className="mt-0.5 truncate text-[11px] text-neutral-700">{emp.designation || "—"}</p>
          <p className="mt-1 truncate text-[10px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            {emp.department || "—"}
          </p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!h-1 !w-2 !border-0 !bg-neutral-400" />
    </div>
  );
}

export default memo(EmployeeNodeImpl);
